import { useState } from "react";

import { FormModelInfoWithParameters } from "../types";
import { Provider, Model } from "@/queries/models";
import { useParams } from "react-router-dom";
import { mapModelInfoToAPI } from "../utils";
import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { getInferenceClientRequestData } from "./utils";
import { UseFormReturn } from "react-hook-form";
import { CustomModelFormValues } from "../types";
import { useCustomParametersStore } from "../CustomModelsCreation/CustomParameters/custom-parameters-store";
import { useEditCustomModel } from "@/queries/models/useEditCustomModel";
import { useCreateCustomModel } from "@/queries/models/useCreateCustomModel";
import { useAuth } from "@/context/auth-context";
import { useInferenceClients } from "@/queries";

export const useCustomModelsForm = ({
  setIsOpen,
  methods,
  setActiveStep,
}: {
  setIsOpen: (isOpen: boolean) => void;
  methods: UseFormReturn<CustomModelFormValues>;
  setActiveStep: (activeStep: number) => void;
}) => {
  const store = useCustomParametersStore();
  const { workspaceId } = useParams();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const authContext = useAuth();
  const { mutate: createCustomModel, isLoading: isCreatingPending } =
    useCreateCustomModel(authContext?.userToken!);
  const { mutate: editCustomModel, isLoading: isEditingPending } =
    useEditCustomModel(authContext?.userToken!);

  const {
    mutate: createInferenceClient,
    isLoading: isCreatingInferenceClientPending,
  } = useInferenceClients(authContext?.userToken!);

  const reset = () => {
    setIsSubmitted(false);
    setIsOpen(false);
    store.reset();
    methods.reset();
    setActiveStep(0);
    store.isEditing && store.setIsEditing(false);
  };

  const onSubmit = () => {
    const data = methods.getValues("create_custom_model");
    const provider = store.modelsList.find(
      (provider: Provider) => provider.provider_name === data.provider,
    );
    const model = provider?.model_configs.find(
      (model: Model) => model.id === data.id,
    );

    const combinedData = {
      ...data,
      id: model?.id,
      parameters: store.parametersList.map(
        (parameter) => parameter.getParameter,
      ),
    } as FormModelInfoWithParameters;

    const mappedModelInfo = mapModelInfoToAPI(
      combinedData,
      Number(workspaceId),
      provider?.provider_id || 0,
    );

    if (store.isEditing) {
      editCustomModel(mappedModelInfo, { onSuccess: reset });
      return;
    }

    createCustomModel(mappedModelInfo, {
      onSuccess: () => {
        if (data.provider === HUGGINGFACE_PROVIDER) {
          const inferenceClientRequestData = getInferenceClientRequestData(
            combinedData,
            store.headers,
            store.cookies,
            store.timeout,
            workspaceId ?? "",
          );

          createInferenceClient(inferenceClientRequestData, {
            onSuccess: reset,
          });

          return;
        }

        reset();
      },
    });
  };

  return {
    isSubmitted,
    setIsSubmitted,
    onSubmit,
    reset,
    isLoading:
      isCreatingPending || isEditingPending || isCreatingInferenceClientPending,
  };
};
